import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Share, ExternalLink, Copy, MoreVertical, MonitorSmartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import hudyMotionLogo from "@/assets/hudy-motion-logo.png";
import { TypeAnimation } from 'react-type-animation';

const PWAInstallPrompt = () => {
 const isHudyMotion = window.location.pathname === "/hudymotion";

 const [deferredPrompt, setDeferredPrompt] = useState<any | null>(null);
 const [showPrompt, setShowPrompt] = useState(false);
 const [isIOS, setIsIOS] = useState(false);
 const [isInAppBrowser, setIsInAppBrowser] = useState(false);
 const [dismissed, setDismissed] = useState(false);
 const [showManualAndroid, setShowManualAndroid] = useState(false);

 const isAndroid = /android/i.test(window.navigator.userAgent);


  useEffect(() => {
    const handleForcePrompt = () => {
      setShowPrompt(true);
      setDismissed(false);
      setShowManualAndroid(false);
    };
    window.addEventListener('force-pwa-prompt', handleForcePrompt);
    return () => window.removeEventListener('force-pwa-prompt', handleForcePrompt);
  }, []);


 useEffect(() => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
  setIsIOS(isIOSDevice);
  const isInApp = /instagram|fbav|fb_iab|messenger|threads/.test(userAgent);
  setIsInAppBrowser(isInApp);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  if (isStandalone) return;
  if (window.location.pathname.includes("/overlay")) return;
  const wasDismissed = localStorage.getItem("pwa-install-dismissed");
  if (wasDismissed) return;

  if (isInApp) {
   const timer = setTimeout(() => setShowPrompt(true), 10000);
   return () => clearTimeout(timer);
  }
  if (isIOSDevice) {
   const timer = setTimeout(() => setShowPrompt(true), 10000);
   return () => clearTimeout(timer);
  } else {
   const handler = (e: Event) => {
    e.preventDefault();
    setDeferredPrompt(e as any);
    const timer = setTimeout(() => setShowPrompt(true), 10000);
    return () => clearTimeout(timer);
   };
   window.addEventListener("beforeinstallprompt", handler);
   const timer = setTimeout(() => {
    if (!deferredPrompt && !isStandalone && !wasDismissed) setShowPrompt(true);
   }, 10000);
   return () => {
    window.removeEventListener("beforeinstallprompt", handler);
    clearTimeout(timer);
   };
  }
 }, [deferredPrompt]);

 const handleInstall = async () => {
  if (deferredPrompt) {
   await deferredPrompt.prompt();
   const choice = await deferredPrompt.userChoice;
   if (choice.outcome === "accepted") {
     setShowPrompt(false);
   }
  } else {
    setShowManualAndroid(true);
  }
 };

 const handleDismiss = () => {
  localStorage.setItem("pwa-install-dismissed", "true");
  setDismissed(true);
  setShowPrompt(false);
 };

 const handleOpenExternal = () => {
  window.open(window.location.href, "_blank");
 };

 const handleCopyLink = () => {
  navigator.clipboard.writeText(window.location.href).then(() => {
   toast.success("Link copiado!");
  });
 };

 if (dismissed) return null;

 // ====================== HUDY MOTION THEME ======================
 if (isHudyMotion) {
  return (
   <AnimatePresence>
    {showPrompt && (
     <motion.div
      initial={{ opacity: 0, y: 80, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 80, scale: 0.95 }}
      transition={{ type: "spring", damping: 20 }}
      className="fixed bottom-6 right-6 z-[9999] w-[320px] max-w-[calc(100vw-2rem)]"
     >
      <div className="relative bg-[#0d0d0d] border border-red-900/40 rounded-2xl shadow-[0_20px_60px_rgba(220,38,38,0.3)] overflow-hidden">
       <div className="h-1 w-full bg-gradient-to-r from-red-800 via-red-500 to-red-800" />
       <button onClick={handleDismiss} className="absolute top-3 right-3 text-white/40 hover:text-white transition-colors p-1 bg-white/5 rounded-full z-10">
        <X className="w-4 h-4" />
       </button>
       <div className="p-5">
        <div className="flex items-center gap-3 mb-4">
         <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-black border border-red-900/30 p-1">
          <img src={hudyMotionLogo} alt="Hudy Motion" className="w-full h-full object-contain" />
         </div>
         <div>
          <h3 className="text-white font-black text-base leading-tight">HUDY MOTION</h3>
          <p className="text-white/40 text-xs mt-0.5">Adicione {'\u00E0'} Tela Inicial</p>
         </div>
        </div>
        <p className="text-white/60 text-sm mb-5 leading-relaxed">
         Instale o <strong className="text-white">Hudy Motion</strong> no seu celular e crie v{'\u00ED'}deos incr{'\u00ED'}veis com 1 toque!
        </p>
        {isIOS ? (
         <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3 text-xs text-white/80 font-medium">
           <div className="w-7 h-7 rounded-lg bg-red-600/20 flex items-center justify-center flex-shrink-0">
            <Share className="w-4 h-4 text-red-400" />
           </div>
           <span>1. Toque em <strong>"Compartilhar"</strong> no Safari.</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-white/80 font-medium">
           <div className="w-7 h-7 rounded-lg bg-red-600/20 flex items-center justify-center flex-shrink-0">
            <Download className="w-4 h-4 text-red-400" />
           </div>
           <span>2. Selecione <strong>"Adicionar {'\u00E0'} Tela de In{'\u00ED'}cio"</strong>.</span>
          </div>
         </div>
        ) : showManualAndroid ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
           <div className="flex items-center gap-3 text-xs text-white/80 font-medium">
            <div className="w-7 h-7 rounded-lg bg-red-600/20 flex items-center justify-center flex-shrink-0">
             <MoreVertical className="w-4 h-4 text-red-400" />
            </div>
            <span>1. Toque nos <strong>tr{'\u00EA'}s pontinhos</strong> (menu superior).</span>
           </div>
           <div className="flex items-center gap-3 text-xs text-white/80 font-medium">
            <div className="w-7 h-7 rounded-lg bg-red-600/20 flex items-center justify-center flex-shrink-0">
             <MonitorSmartphone className="w-4 h-4 text-red-400" />
            </div>
            <span>2. Selecione <strong>"Adicionar {'\u00E0'} tela inicial"</strong>.</span>
           </div>
          </div>
        ) : (
         <button
          onClick={handleInstall}
          className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-black rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 shadow-[0_4px_20px_rgba(220,38,38,0.4)]"
         >
          <Download className="w-5 h-5" />
          {deferredPrompt ? "INSTALAR AGORA" : "COMO INSTALAR"}
         </button>
        )}
       </div>
      </div>
     </motion.div>
    )}
   </AnimatePresence>
  );
 }

 // ====================== DEFAULT THEME (other pages) ======================
 return (
  <AnimatePresence>
   {showPrompt && (
    <motion.div
     initial={{ opacity: 0, y: 80, scale: 0.95 }}
     animate={{ opacity: 1, y: 0, scale: 1 }}
     exit={{ opacity: 0, y: 80, scale: 0.95 }}
     transition={{ type: "spring", damping: 20 }}
     className="fixed bottom-6 right-6 z-[9999] w-[320px] max-w-[calc(100vw-2rem)] rounded-[24px] bg-card border border-border shadow-2xl overflow-hidden p-6"
    >
     <button onClick={handleDismiss} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors p-1 bg-foreground/10 rounded-full z-10">
      <X className="w-4 h-4" />
     </button>
     <div className="flex items-start gap-5">
      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/20">
       <Download className="w-7 h-7 text-black" />
      </div>
      <div className="flex-1 pt-1">
       <h3 className="text-foreground font-black text-xl mb-1 leading-tight tracking-tight uppercase ">
        <TypeAnimation sequence={["INSTALAR APP", 2000, "BAIXAR APP", 2000]} wrapper="span" cursor={true} repeat={Infinity} />
       </h3>
       {isInAppBrowser ? (
        <div className="space-y-4">
         <p className="text-muted-foreground text-xs leading-relaxed font-medium">
          {isAndroid
           ? "O Instagram bloqueia a instala\u00E7\u00E3o. Clique no bot\u00E3o abaixo para abrir no Chrome e baixar o app."
           : "O Instagram n\u00E3o permite instalar apps. Copie o link e cole no seu navegador Safari."}
         </p>
         <div className="flex flex-col gap-2">
          {isAndroid ? (
           <Button onClick={handleOpenExternal} className="bg-primary hover:bg-primary/90 text-primary-foreground font-black rounded-2xl w-full gap-2 text-xs py-5 uppercase  shadow-lg shadow-primary/20">
            <ExternalLink className="w-4 h-4" /> Abrir no Chrome e Instalar
           </Button>
          ) : (
           <Button onClick={handleCopyLink} variant="outline" className="border-primary/30 text-primary hover:bg-primary/10 font-bold rounded-2xl w-full gap-2 text-xs py-5 uppercase ">
            <Copy className="w-4 h-4" /> Copiar Link do App
           </Button>
          )}
         </div>
        </div>
       ) : (
        <>
         <p className="text-muted-foreground text-sm mb-5 leading-relaxed">
          Adicione o <strong>By Hudson Raone</strong> {'\u00E0'} sua tela inicial para uma experi{'\u00EA'}ncia completa.
         </p>
         {isIOS ? (
          <div className="bg-foreground/10 border border-foreground/10 rounded-2xl p-4 space-y-3">
           <div className="flex items-center gap-3 text-[11px] text-foreground/90 font-medium">
            <div className="w-7 h-7 rounded-lg bg-foreground/10 flex items-center justify-center flex-shrink-0">
             <Share className="w-4 h-4 text-primary" />
            </div>
            <span>1. Toque em <strong>"Compartilhar"</strong> no Safari.</span>
           </div>
           <div className="flex items-center gap-3 text-[11px] text-foreground/90 font-medium">
            <div className="w-7 h-7 rounded-lg bg-foreground/10 flex items-center justify-center flex-shrink-0">
             <Download className="w-4 h-4 text-primary" />
            </div>
            <span>2. Selecione <strong>"Adicionar {'\u00E0'} Tela de In{'\u00ED'}cio"</strong>.</span>
           </div>
          </div>
         ) : showManualAndroid ? (
          <div className="bg-foreground/10 border border-foreground/10 rounded-2xl p-4 space-y-3">
           <div className="flex items-center gap-3 text-[11px] text-foreground/90 font-medium">
            <div className="w-7 h-7 rounded-lg bg-foreground/10 flex items-center justify-center flex-shrink-0">
             <MoreVertical className="w-4 h-4 text-primary" />
            </div>
            <span>1. Toque nos <strong>tr{'\u00EA'}s pontinhos</strong> (Menu).</span>
           </div>
           <div className="flex items-center gap-3 text-[11px] text-foreground/90 font-medium">
            <div className="w-7 h-7 rounded-lg bg-foreground/10 flex items-center justify-center flex-shrink-0">
             <MonitorSmartphone className="w-4 h-4 text-primary" />
            </div>
            <span>2. Selecione <strong>"Adicionar {'\u00E0'} tela inicial"</strong>.</span>
           </div>
          </div>
         ) : (
          <Button onClick={handleInstall} size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-black rounded-2xl w-full shadow-lg shadow-primary/20 transition-all active:scale-[0.97] uppercase ">
           {deferredPrompt ? "Instalar Agora" : "Como Instalar"}
          </Button>
         )}
        </>
       )}
      </div>
     </div>
    </motion.div>
   )}
  </AnimatePresence>
 );
};

export default PWAInstallPrompt;
